// src/pages/sessions/components/LiveSession/whiteboard/WhiteboardContextMenu.jsx
import React, { useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';

// Icons as inline SVGs for the context menu
const TrashIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.431.992a7.723 7.723 0 010 .255c-.007.378.138.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResizeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BringToFrontIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 19.5v-15m0 0l-6.75 6.75M12 4.5l6.75 6.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SendToBackIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 4.5v15m0 0l6.75-6.75M12 19.5l-6.75-6.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = ({ locked }) => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    {locked ? (
      <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

/**
 * WhiteboardContextMenu - Floating context menu for selected whiteboard objects
 * Positioned above the selected object, similar to Classroom Screen's widget menu
 */
const WhiteboardContextMenu = memo(({
  visible,
  position, // { x, y } in screen coordinates
  isLocked,
  onRemove,
  onCopy,
  onBringToFront,
  onSendToBack,
  onToggleLock,
  onResizeToFit,
  onClose,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    // Delay to avoid immediate close from the same click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible || !position) return null;

  const MenuItem = ({ icon, label, shortcut, onClick, danger }) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
        onClose?.();
      }}
      className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
        danger 
          ? 'text-red-400 hover:bg-red-500/10' 
          : 'text-gray-200 hover:bg-white/10'
      }`}
    >
      <span className="flex items-center gap-2.5">
        {icon}
        {label}
      </span>
      {shortcut && (
        <span className="text-xs text-gray-500 ml-4 font-mono bg-gray-700/50 px-1.5 py-0.5 rounded">
          {shortcut}
        </span>
      )}
    </button>
  );

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%) translateY(-8px)',
      }}
    >
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-600/50 overflow-hidden min-w-[220px]">
        <MenuItem
          icon={<TrashIcon />}
          label={t('whiteboard.context.remove', 'Remove')}
          shortcut="⌫"
          onClick={onRemove}
          danger
        />
        
        <div className="h-px bg-gray-700/50 mx-2" />
        
        <MenuItem
          icon={<ResizeIcon />}
          label={t('whiteboard.context.resizeToFit', 'Resize to fit')}
          onClick={onResizeToFit}
        />
        <MenuItem
          icon={<CopyIcon />}
          label={t('whiteboard.context.copy', 'Copy widget')}
          shortcut="Ctrl C"
          onClick={onCopy}
        />
        <MenuItem
          icon={<LockIcon locked={isLocked} />}
          label={isLocked 
            ? t('whiteboard.context.unlock', 'Unlock position') 
            : t('whiteboard.context.lock', 'Lock in position')}
          shortcut="Shift L"
          onClick={onToggleLock}
        />
        
        <div className="h-px bg-gray-700/50 mx-2" />
        
        <MenuItem
          icon={<BringToFrontIcon />}
          label={t('whiteboard.context.bringToFront', 'Bring to front')}
          shortcut="Ctrl ↑"
          onClick={onBringToFront}
        />
        <MenuItem
          icon={<SendToBackIcon />}
          label={t('whiteboard.context.sendToBack', 'Send to back')}
          shortcut="Ctrl ↓"
          onClick={onSendToBack}
        />
      </div>
    </div>
  );
});

WhiteboardContextMenu.displayName = 'WhiteboardContextMenu';

export default WhiteboardContextMenu;
